
































































































import {Component, Prop, Watch} from 'vue-property-decorator';
import CommonVue from '../models/commonVue';
import School from '../api/school';
import ApiList from '../api/list';
import LockManager from '../models/lock';

import {BusEvent} from '@/models/bus';
import FieldInternational from '../components/FieldInternational.vue';
import DropDownEntity from '../components/DropDownEntity.vue';
import DropDownText from '../components/DropDownText.vue';
import EditIcon from '../components/icons/EditIcon.vue';
import Category from "@/api/category";
import Topic from "@/api/topic";
import FieldImage from "@/components/FieldImage.vue";
import Media from "@/api/media";
import {MediaFileField} from "@/models/fields";

@Component({
    components: {
        'drop-down-entity': DropDownEntity,
        'drop-down-text': DropDownText,
        'field-international': FieldInternational,
        'edit-icon' : EditIcon,
        'field-image': FieldImage,
    },
})
export default class EditCategory extends CommonVue {
    @Prop({type: Number}) public id?: number;

    public schoolList: ApiList<School> = null;
    public category: Category = null;
    public categoryName: string = '';

    public options: object[] = null;

    // do not show schools if there is only 1 school
    public showSchools: boolean = false;

    public async beforeMount() {
        await this.load();
    }

    @Watch('id')
    public async load() {
        this.busy = true;
        this.error = false;

        this.ignoreFirstEntityChange = true;
        await this.loadCategory();
        this.ignoreFirstEntityChange = false;
        this.updateName();

        if (this.category && this.category.id) {
            this.lockManager = new LockManager(this.CATEGORY_SLUG, this.category.id);
            await this.lockManager.checkStatus();
            if (this.lockManager.isBusy) {
                this.$bus.$emit(BusEvent.ERROR, 'Someone else is making changes.', new Error('Click refresh to check if they have finished their changes'));
            }
        } else {
            this.lockManager = null;
        }

        await this.loadSchoolList();

        this.busy = false;
    }

    public async loadCategory(): Promise<void> {
        if (!this.id) {
            this.category = new Category();
        } else {
            try {
                this.category = await Category.get(this.id);
            }
            catch (e) {
                this.error = true;
                this.$bus.$emit(BusEvent.ERROR, 'Could not get topic information', e);
            }
        }
    }

    public updateName() {
        let name = this.category.name.getDefaultValue();
        if (!name) {
            name = '';
        }
        this.categoryName = name;
    }

    public async loadSchoolList() {
        try {
            this.schoolList = await School.listAll();

            // hide school field if there is only 1 school and assign it as default
            if (this.category && this.schoolList && this.schoolList.length === 1) {
                this.category.school = this.schoolList.getIndex(0).id;
            } else {
                this.showSchools = true;
            }
        }
        catch (e) {
            this.$bus.$emit(BusEvent.ERROR, 'Could not load schools list', e);
            this.error = true;
        }
    }

    // enter edit mode if it can get the lock
    @Watch('category', {deep: true})
    public async enterEditMode() {
        await this.tryToLock();
    }

    public async deleteClicked(): Promise<void> {
        if (!this.category || !this.category.id) {
            return;
        }

        this.busy = true;

        try {
            if (!this.lockManager){
                this.lockManager = new LockManager(this.CATEGORY_SLUG, this.category.id);
            }

            await this.tryToLock();

            await this.category.delete();

            await this.lockManager.releaseLock();

            this.busy = false;
            this.$router.push(`/${this.CATEGORY_SLUG}/`);
        }
        catch (e) {
            this.busy = false;
            this.$bus.$emit(BusEvent.ERROR, 'Could not delete category', e);
        }
    }

    public async saveClicked(): Promise<void> {
        if (!this.category) {
            return;
        }

        this.busy = true;

        try {
            this.checkMandatoryFields();

            await this.saveCategory();

            this.busy = false;
            this.$router.push(`/${this.CATEGORY_SLUG}/${this.category.id}`);

        } catch (e) {
            this.busy = false;
            this.$bus.$emit(BusEvent.ERROR, 'Could not save topic', e);
        }
    }

    public checkMandatoryFields() {
        if(!this.category){
            throw Error('Unable to create Category.');
        }

        // Check if name is filled
        const defaultName = this.category.name.getDefaultValue();
        if(defaultName === undefined || defaultName === '') {
            throw Error('Please insert an English name for your category.');
        }

        // Check if school field is filled
        if(this.category.school === undefined || this.category.school === null) {
            throw Error('Please select a school.');
        }
    }

    public async saveCategory(): Promise<void> {
        if (!this.category) {
            throw Error('Unable to save.');
        }

        const isNew = !this.category.id;

        if (!isNew) {
            if (!this.lockManager && this.category && this.category.id){
                this.lockManager = new LockManager(this.CATEGORY_SLUG, this.category.id);
            }
            await this.tryToLock();
            await this.saveMediaFiles();
        }

        await this.category.save();

        if (isNew) {
            await this.saveCategory();
        }

        if (this.lockManager && this.lockManager.isLocked) {
            await this.lockManager.releaseLock();
        }
    }

    public async saveMediaFiles(): Promise<void> {
        if (!this.category || !this.category.id) {
            throw Error('Topic has not been created yet.');
        }

        this.imagesToDelete.forEach(async (id) => await Media.deleteImage(id));
        this.imagesToDelete = [];

        this.audioToDelete.forEach(async (id) => await Media.deleteAudio(id));
        this.audioToDelete = [];

        const bg_image = await this.saveMediaFile(this.$refs.bg_image, 'bg_image');
        if (bg_image !== null) {
            this.category.bg_image = bg_image;
        }

    }

    public async saveMediaFile(field: any, fieldName: string): Promise<number|null|undefined> {
        if (!this.category || !this.category.id){
            return null;
        }
        const mediaFileField = (field as any) as MediaFileField;
        if (mediaFileField.hasNewMedia()) {
            return await mediaFileField.upload(Category.SLUG, this.category.id, fieldName);
        } else if (!mediaFileField.hasMedia()) {
            return undefined;
        } else {
            return null;
        }
    }

}
